import { supabase } from '../../lib/supabase';
export class SalesActivitiesService {
    /**
     * Get activities for an organization with optional filtering
     */
    async getActivities(organizationId, filters) {
        try {
            let query = supabase
                .from('sales_activities')
                .select('*')
                .eq('organization_id', organizationId)
                .order('created_at', { ascending: false })
                .limit(100);
            // Apply filters
            if (filters?.activityType && filters.activityType.length > 0) {
                query = query.in('activity_type', filters.activityType);
            }
            if (filters?.contactId) {
                query = query.eq('contact_id', filters.contactId);
            }
            if (filters?.dealId) {
                query = query.eq('deal_id', filters.dealId);
            }
            if (filters?.dateRange) {
                query = query
                    .gte('created_at', filters.dateRange.start.toISOString())
                    .lte('created_at', filters.dateRange.end.toISOString());
            }
            const { data, error } = await query;
            if (error) {
                console.error('Error fetching activities:', error);
                throw error;
            }
            return this.transformActivities(data || []);
        }
        catch (error) {
            console.error('Error in getActivities:', error);
            return [];
        }
    }
    /**
     * Get recent activities for the Today/Agenda view
     */
    async getRecentActivities(organizationId, limit = 20) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .select('*')
                .eq('organization_id', organizationId)
                .order('created_at', { ascending: false })
                .limit(limit);
            if (error) {
                console.error('Error fetching recent activities:', error);
                return [];
            }
            return this.transformActivities(data || []);
        }
        catch (error) {
            console.error('Error in getRecentActivities:', error);
            return [];
        }
    }
    /**
     * Get unread emails
     */
    async getUnreadEmails(organizationId) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('activity_type', 'email')
                .eq('direction', 'inbound')
                .eq('status', 'completed')
                .order('created_at', { ascending: false })
                .limit(50);
            if (error) {
                console.error('Error fetching unread emails:', error);
                return [];
            }
            return this.transformActivities(data || []);
        }
        catch (error) {
            console.error('Error in getUnreadEmails:', error);
            return [];
        }
    }
    /**
     * Create a new activity
     */
    async createActivity(organizationId, userId, request) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .insert({
                organization_id: organizationId,
                contact_id: request.contactId,
                deal_id: request.dealId,
                activity_type: request.activityType,
                subject: request.subject,
                body: request.body,
                scheduled_at: request.scheduledAt?.toISOString(),
                status: request.scheduledAt ? 'scheduled' : 'completed',
                created_by: userId,
                assigned_to: userId
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating activity:', error);
                throw error;
            }
            return this.transformActivity(data);
        }
        catch (error) {
            console.error('Error in createActivity:', error);
            return null;
        }
    }
    /**
     * Log an email activity
     */
    async logEmail(organizationId, userId, contactId, emailData) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .insert({
                organization_id: organizationId,
                contact_id: contactId,
                activity_type: 'email',
                direction: emailData.direction,
                subject: emailData.subject,
                body: emailData.body,
                email_from: emailData.from,
                email_to: emailData.to,
                email_cc: emailData.cc,
                email_thread_id: emailData.threadId,
                email_message_id: emailData.messageId,
                status: 'completed',
                created_by: userId
            })
                .select()
                .single();
            if (error) {
                console.error('Error logging email:', error);
                throw error;
            }
            return this.transformActivity(data);
        }
        catch (error) {
            console.error('Error in logEmail:', error);
            return null;
        }
    }
    /**
     * Log a call activity
     */
    async logCall(organizationId, userId, contactId, callData) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .insert({
                organization_id: organizationId,
                contact_id: contactId,
                activity_type: 'call',
                direction: callData.direction,
                subject: callData.subject,
                body: callData.notes,
                duration: callData.duration,
                outcome: callData.outcome,
                status: 'completed',
                created_by: userId,
                completed_at: new Date().toISOString()
            })
                .select()
                .single();
            if (error) {
                console.error('Error logging call:', error);
                throw error;
            }
            return this.transformActivity(data);
        }
        catch (error) {
            console.error('Error in logCall:', error);
            return null;
        }
    }
    /**
     * Create a note
     */
    async createNote(organizationId, userId, contactId, dealId, noteContent) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .insert({
                organization_id: organizationId,
                contact_id: contactId,
                deal_id: dealId,
                activity_type: 'note',
                body: noteContent,
                status: 'completed',
                created_by: userId
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating note:', error);
                throw error;
            }
            return this.transformActivity(data);
        }
        catch (error) {
            console.error('Error in createNote:', error);
            return null;
        }
    }
    /**
     * Get last contact activity for a specific contact
     */
    async getLastContactActivity(organizationId, contactId) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('contact_id', contactId)
                .in('activity_type', ['email', 'call', 'meeting', 'sms', 'linkedin'])
                .order('created_at', { ascending: false })
                .limit(1)
                .single();
            if (error) {
                if (error.code === 'PGRST116') {
                    // No activities found
                    return null;
                }
                console.error('Error fetching last contact activity:', error);
                return null;
            }
            return this.transformActivity(data);
        }
        catch (error) {
            console.error('Error in getLastContactActivity:', error);
            return null;
        }
    }
    /**
     * Get activity statistics
     */
    async getActivityStats(organizationId) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .select('activity_type, created_at')
                .eq('organization_id', organizationId);
            if (error) {
                console.error('Error fetching activity stats:', error);
                throw error;
            }
            const activities = data || [];
            const totalActivities = activities.length;
            // Count by type
            const activitiesByType = activities.reduce((acc, activity) => {
                const existing = acc.find(a => a.type === activity.activity_type);
                if (existing) {
                    existing.count++;
                }
                else {
                    acc.push({
                        type: activity.activity_type,
                        count: 1
                    });
                }
                return acc;
            }, []);
            // Get recent activities
            const recentActivities = await this.getRecentActivities(organizationId, 10);
            return {
                totalActivities,
                activitiesByType,
                recentActivities
            };
        }
        catch (error) {
            console.error('Error in getActivityStats:', error);
            return {
                totalActivities: 0,
                activitiesByType: [],
                recentActivities: []
            };
        }
    }
    /**
     * Subscribe to real-time activity changes
     */
    subscribeToActivities(organizationId, callback) {
        return supabase
            .channel(`sales_activities:${organizationId}`)
            .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'sales_activities',
            filter: `organization_id=eq.${organizationId}`
        }, (payload) => {
            if (payload.new) {
                callback(this.transformActivity(payload.new));
            }
        })
            .subscribe();
    }
    /**
     * Transform database row to SalesActivity type
     */
    transformActivity(row) {
        return {
            id: row.id,
            organizationId: row.organization_id,
            contactId: row.contact_id,
            dealId: row.deal_id,
            activityType: row.activity_type,
            direction: row.direction,
            subject: row.subject,
            body: row.body,
            emailFrom: row.email_from,
            emailTo: row.email_to,
            emailCc: row.email_cc,
            emailBcc: row.email_bcc,
            emailMessageId: row.email_message_id,
            emailThreadId: row.email_thread_id,
            emailAttachments: row.email_attachments,
            duration: row.duration,
            location: row.location,
            meetingUrl: row.meeting_url,
            scheduledAt: row.scheduled_at ? new Date(row.scheduled_at) : undefined,
            completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
            status: row.status,
            outcome: row.outcome,
            aiSummary: row.ai_summary,
            aiSentiment: row.ai_sentiment,
            aiNextSteps: row.ai_next_steps,
            createdBy: row.created_by,
            assignedTo: row.assigned_to,
            customFields: row.custom_fields || {},
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at)
        };
    }
    /**
     * Transform multiple database rows
     */
    transformActivities(rows) {
        return rows.map(row => this.transformActivity(row));
    }
}
// Export singleton instance
export const salesActivitiesService = new SalesActivitiesService();
